export enum NoteType {
  GENERAL = 'general',
  WARNING = 'warning',
  IMPORTANT = 'important',
  FOLLOW_UP = 'follow_up',
  REMINDER = 'reminder',
  INTERNAL = 'internal'
}

export const ALLOWED_ENTITY_TYPES = [
  'party',
  'voucher',
  'task',
  'document',
  'item',
  'quotation',
  'sales_order',
  'purchase_order',
  'delivery_challan',
  'account',
  'compliance_service',
  'party_subscription'
] as const;

export type EntityType = typeof ALLOWED_ENTITY_TYPES[number];

export interface NoteUser {
  id: number;
  username: string;
  full_name?: string;
}

export interface Note {
  id: number;
  organization_id: number;
  site_id?: number | null;
  entity_type: EntityType;
  entity_id: number;
  title?: string | null;
  content: string;
  note_type: NoteType;
  extra_data?: Record<string, any> | null;
  is_pinned: boolean;
  is_private: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at?: string | null;
  created_by: number;
  updated_by?: number | null;
  created_by_user?: NoteUser;
  updated_by_user?: NoteUser | null;
}

export interface NoteCreate {
  entity_type: EntityType;
  entity_id: number;
  title?: string;
  content: string;
  note_type?: NoteType;
  is_pinned?: boolean;
  is_private?: boolean;
  site_id?: number | null;
  extra_data?: {
    tags?: string[];
    priority?: 'low' | 'medium' | 'high' | 'critical';
    category?: string;
    due_date?: string;
    custom_fields?: Record<string, any>;
  };
}

export interface NoteUpdate {
  title?: string;
  content?: string;
  note_type?: NoteType;
  extra_data?: Record<string, any>;
  is_pinned?: boolean;
  is_private?: boolean;
}

export interface NoteFilters {
  entity_type?: EntityType;
  entity_id?: number;
  note_type?: NoteType;
  is_pinned?: boolean;
  is_private?: boolean;
  is_deleted?: boolean;
  created_by?: number;
  search?: string;
  page?: number;
  size?: number;
}

export interface NotesListResponse {
  items: Note[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Helper functions for note type styling
export const getNoteTypeColor = (type: NoteType): string => {
  switch (type) {
    case NoteType.WARNING:
      return 'text-orange-600 bg-orange-50 border-orange-200';
    case NoteType.IMPORTANT:
      return 'text-red-600 bg-red-50 border-red-200';
    case NoteType.FOLLOW_UP:
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case NoteType.REMINDER:
      return 'text-purple-600 bg-purple-50 border-purple-200';
    case NoteType.INTERNAL:
      return 'text-gray-600 bg-gray-50 border-gray-200';
    case NoteType.GENERAL:
    default:
      return 'text-gray-600 bg-white border-gray-200';
  }
};

export const getNoteTypeLabel = (type: NoteType): string => {
  switch (type) {
    case NoteType.FOLLOW_UP:
      return 'Follow Up';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};